import { Space, Spin, notification } from "antd";
import dayjs from "dayjs";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { API_DATE_FORMAT, DATE_FORMAT } from "utils/constants";
import { isProjectRia, resolvePlanDateDeltas } from "utils/helpers/array";
import {
  isBooleanFieldChecked,
  toBooleanFieldValue,
} from "utils/helpers/booleanField";
import {
  evaluateField,
  evaluateFormDefinition,
  isFieldVisible,
  resolveInputProps,
  usesPlanDeltas,
} from "utils/validation-rules";
import { resolveFormFieldValue } from "./resolveFormFieldValue";
import usePlanCardOptions from "./usePlanCardOptions";

const EMPTY_DISPLAY = "—";
const TELECOM_EMAIL = "EMAIL";
const TELECOM_PHONE = "PHONE";
const TELECOM_MOBILE = "MOBILE";
const CUSTOM_ENTITY_KEY = "custom";
const isEmptyFormValue = (value) => isNil(value) || value === "";

const variableAccessor = (entryKey, field) => {
  if (entryKey === CUSTOM_ENTITY_KEY)
    return `${CUSTOM_ENTITY_KEY}.${field.key}`;
  const target = field.target;
  if (!target || !target.property) return `${entryKey}.${field.key}`;
  if (target.entity && target.entity !== "project")
    return [target.entity, target.property, target.filterValue]
      .filter(Boolean)
      .join(".");
  return target.property;
};

const registryToEntities = (registry = []) =>
  registry.flatMap((entry) =>
    (entry.fields || []).flatMap((field) => {
      const base = {
        entity: entry.key,
        field: field.key,
        target: field.target || null,
        options: field.options || null,
        mode: field.mode || null,
        type: field.type || null,
        required: !!field.required,
        defaultRules: field.defaultRules || null,
        inputConfig: field.inputConfig || null,
      };
      const targetAccessor = variableAccessor(entry.key, field);
      const keyAccessor = `${entry.key}.${field.key}`;
      const rows = [{ ...base, accessor: targetAccessor }];
      if (keyAccessor !== targetAccessor) {
        rows.push({ ...base, accessor: keyAccessor });
      }
      return rows;
    }),
  );

const enrichFormField = (field, meta) => {
  if (!meta) return field;
  const rules =
    field.rules && field.rules.length ? field.rules : meta.defaultRules || [];
  return {
    ...field,
    required: field.required || meta.required || false,
    type: field.type || meta.type || null,
    options: field.options || meta.options || null,
    rules,
    inputConfig: {
      ...(meta.inputConfig || {}),
      ...(field.inputConfig || {}),
    },
  };
};

const resolveInputType = (field, meta) => {
  if (field.type === "cards") return "radioGroup";
  if (field.type) return field.type;
  if (meta && meta.type === "cards") return "radioGroup";
  if (meta && meta.type) return meta.type;
  const options = field.options || get(meta, "options");
  if (options && options.length) return "select";
  const entity = get(meta, "target.entity");
  const property = get(meta, "target.property") || "";
  const filterValue = (get(meta, "target.filterValue") || "").toUpperCase();
  if (/date$/i.test(property)) return "date";
  if (entity === "telecom") {
    if (filterValue === TELECOM_EMAIL) return "email";
    if (filterValue === TELECOM_PHONE || filterValue === TELECOM_MOBILE)
      return "phone";
  }
  return "text";
};

const formatDisplay = (value) => {
  if (isNil(value)) return EMPTY_DISPLAY;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const isSelectedPlanField = (accessor) =>
  accessor === "project.selectedPlan" || accessor === "fields.selectedPlan";

const setPathValue = (object, path, value) => {
  if (!path) return object;
  const parts = String(path).split(".");
  let current = object;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (isNil(current[key]) || typeof current[key] !== "object") {
      current[key] = {};
    }
    current = current[key];
  }
  current[parts[parts.length - 1]] = value;
  return object;
};

const buildContext = (project, values) => {
  const fields = get(project, "fields", {}) || {};
  const custom = { ...fields };
  const draftPrefix = "project.fields.";
  Object.keys(fields).forEach((key) => {
    if (key.indexOf(draftPrefix) === 0) {
      custom[key.slice(draftPrefix.length)] = fields[key];
    }
  });
  const context = {
    ...(project || {}),
    fields: { ...fields },
    custom,
  };
  Object.entries(values || {}).forEach(([accessor, value]) => {
    setPathValue(context, accessor, value);
  });
  return context;
};

const CanvasForm = ({
  formDefinition,
  project,
  data = {},
  advance,
  onBack,
  showBack = false,
  isLastStep = false,
  fieldErrors: serverFieldErrors,
  processId,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const RUN = t("RUN", { returnObjects: true });
  const VALIDATION_MESSAGES = tErrors("VALIDATIONS", { returnObjects: true });
  const fields = useMemo(
    () => get(formDefinition, "fields") || [],
    [formDefinition],
  );
  const displayFields = useMemo(
    () => fields.filter((field) => field.display),
    [fields],
  );
  const inputFields = useMemo(
    () => fields.filter((field) => !field.display),
    [fields],
  );
  const { data: registryData } = useQuery(graphql.queries.FIELD_REGISTRY, {
    variables: processId ? { process: { id: processId } } : {},
  });
  const metaByAccessor = useMemo(() => {
    const entries = registryToEntities(get(registryData, "fieldRegistry"));
    return entries.reduce((map, entry) => {
      map[entry.accessor] = entry;
      return map;
    }, {});
  }, [registryData]);

  const resolveDisplay = (accessor) => {
    const value = resolveFormFieldValue({
      project,
      data,
      accessor,
      meta: metaByAccessor[accessor],
    });
    return value;
  };

  const enrichedFields = useMemo(
    () =>
      inputFields.map((field) =>
        enrichFormField(field, metaByAccessor[field.accessor]),
      ),
    [inputFields, metaByAccessor],
  );

  const enrichedFormDefinition = useMemo(
    () => ({
      ...(formDefinition || {}),
      fields: [...displayFields, ...enrichedFields],
    }),
    [formDefinition, displayFields, enrichedFields],
  );
  const hasPlanCardField = useMemo(
    () =>
      enrichedFields.some((field) => {
        const meta = metaByAccessor[field.accessor];
        const type = resolveInputType(field, meta);
        const options = field.options || get(meta, "options") || [];
        return (
          isSelectedPlanField(field.accessor) ||
          ((type === "radioGroup" || type === "radio") &&
            !options.length &&
            get(project, "fields.selectedToCompare", []).length)
        );
      }),
    [enrichedFields, metaByAccessor, project],
  );
  const { options: planCardOptions, loading: planCardOptionsLoading } =
    usePlanCardOptions({
      project,
      enabled: hasPlanCardField,
    });
  const hasPlanDeltaField = useMemo(
    () => enrichedFields.some((field) => usesPlanDeltas(field.inputConfig)),
    [enrichedFields],
  );
  const { data: plansData } = useQuery(graphql.queries.INSURANCE_PLANS, {
    variables: { where: {} },
    skip: !hasPlanDeltaField,
  });

  const [updateEntity] = useMutation(graphql.mutations.UPDATE_ENTITY);

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [goingBack, setGoingBack] = useState(false);
  const timers = useRef({});
  const previousSignature = useRef("");
  const dateDeltas = useMemo(() => {
    if (!hasPlanDeltaField) return {};
    const insurancePlans = get(plansData, "insurancePlans.data") || [];
    return resolvePlanDateDeltas({
      insurancePlans,
      ria:
        isProjectRia(project) ||
        isProjectRia({
          ria: get(values, "ria"),
          fields: {
            ria: get(values, "fields.ria"),
            "project.ria": get(values, "project.ria"),
          },
        }),
    });
  }, [hasPlanDeltaField, plansData, project, values]);

  const fieldsSignature = useMemo(
    () => fields.map((field) => field.accessor).join("|"),
    [fields],
  );

  useEffect(() => {
    if (isNil(project)) return;
    const signatureChanged = previousSignature.current !== fieldsSignature;
    previousSignature.current = fieldsSignature;

    setValues((current) => {
      const base = signatureChanged ? {} : current;
      const next = { ...base };
      let changed = signatureChanged;
      for (const field of inputFields) {
        const accessor = field.accessor;
        if (!signatureChanged && !isEmptyFormValue(next[accessor])) continue;
        const value = resolveFormFieldValue({
          project,
          data,
          accessor,
          meta: metaByAccessor[accessor],
        });
        if (!isNil(value) && next[accessor] !== value) {
          next[accessor] = value;
          changed = true;
        }
      }
      return changed ? next : current;
    });

    if (signatureChanged) {
      setErrors({});
      setTouched({});
    }
  }, [fieldsSignature, project, inputFields, metaByAccessor, data]);

  useEffect(() => {
    if (!serverFieldErrors || !Object.keys(serverFieldErrors).length) return;
    const translated = Object.entries(serverFieldErrors).reduce(
      (map, [accessor, message]) => {
        const key = String(message || "");
        const nested = key.split(".").reduce((current, part) => {
          if (isNil(current) || typeof current !== "object") return undefined;
          return current[part];
        }, VALIDATION_MESSAGES);
        map[accessor] =
          (typeof nested === "string" && nested) ||
          VALIDATION_MESSAGES[key] ||
          key;
        return map;
      },
      {},
    );
    setErrors(translated);
    setTouched((current) => {
      const next = { ...current };
      Object.keys(serverFieldErrors).forEach((accessor) => {
        next[accessor] = true;
      });
      return next;
    });
  }, [serverFieldErrors, VALIDATION_MESSAGES]);

  const context = useMemo(
    () => buildContext(project, values),
    [project, values],
  );

  const visibleInputFields = useMemo(
    () => enrichedFields.filter((field) => isFieldVisible(field, context)),
    [enrichedFields, context],
  );

  const validateAccessor = (field, value) =>
    evaluateField(field, value, context, VALIDATION_MESSAGES);

  const writeField = (accessor, value) => {
    const meta = metaByAccessor[accessor];
    if (!meta || isNil(project) || isNil(project.id)) {
      return Promise.resolve();
    }
    return updateEntity({
      variables: {
        project: { id: project.id },
        entity: meta.entity,
        field: meta.field,
        // A cleared field is sent as null so the stored value is dropped
        // instead of surviving as a stale value behind an empty input.
        value: isNil(value) ? null : value,
      },
    });
  };

  const onFieldChange = (field, value) => {
    const accessor = field.accessor;
    setValues((current) => ({ ...current, [accessor]: value }));
    setErrors((current) => {
      if (!current[accessor]) return current;
      const next = { ...current };
      delete next[accessor];
      return next;
    });
    if (timers.current[accessor]) clearTimeout(timers.current[accessor]);
    timers.current[accessor] = setTimeout(
      () => writeField(accessor, value),
      500,
    );
  };

  const onFieldBlur = (field) => {
    const accessor = field.accessor;
    setTouched((current) => ({ ...current, [accessor]: true }));
    const error = validateAccessor(field, values[accessor]);
    setErrors((current) => ({ ...current, [accessor]: error }));
  };

  const renderInput = (field) => {
    const accessor = field.accessor;
    const meta = metaByAccessor[accessor];
    const type = resolveInputType(field, meta);
    const value = values[accessor];
    const configProps = resolveInputProps(field.inputConfig || {}, dateDeltas);
    const shared = {
      label: field.label,
      placeholder: field.label,
      required: field.required,
      errors: errors[accessor],
      touched: touched[accessor],
      onBlur: () => onFieldBlur(field),
      ...configProps,
    };
    const options = field.options || get(meta, "options") || [];
    const shouldUsePlanCardOptions =
      isSelectedPlanField(accessor) ||
      ((type === "radioGroup" || type === "radio") &&
        !options.length &&
        get(project, "fields.selectedToCompare", []).length);
    const inputOptions = shouldUsePlanCardOptions ? planCardOptions : options;

    if (type === "radioGroup" || type === "radio") {
      if (
        shouldUsePlanCardOptions &&
        planCardOptionsLoading &&
        !inputOptions.length
      ) {
        return (
          <div className="d-flex flex--column gap-8">
            {field.label ? (
              <div className="canvas-editor__meta">{field.label}</div>
            ) : null}
            <Spin size="small" />
          </div>
        );
      }
      return (
        <Input
          {...shared}
          type="radio"
          variant="group"
          values={inputOptions}
          value={isNil(value) ? undefined : value}
          onChange={(next) => onFieldChange(field, next)}
        />
      );
    }

    if (type === "select") {
      return (
        <Select
          {...shared}
          showSearch
          optionFilterProp="label"
          options={options}
          value={isNil(value) ? undefined : value}
          onChange={(next) => onFieldChange(field, next)}
        />
      );
    }

    if (type === "date") {
      return (
        <Input
          {...shared}
          type="date"
          variant="simple"
          format={DATE_FORMAT}
          value={value ? dayjs(value) : undefined}
          onChange={(date) =>
            onFieldChange(field, date ? date.format(API_DATE_FORMAT) : null)
          }
        />
      );
    }

    if (type === "number" || type === "slider") {
      return (
        <Input
          {...shared}
          type="number"
          value={isNil(value) ? "" : value}
          onChange={(event) => {
            const raw = get(event, "target.value");
            onFieldChange(field, raw === "" || isNil(raw) ? null : Number(raw));
          }}
        />
      );
    }

    if (type === "checkbox" || type === "switch") {
      return (
        <Input
          {...shared}
          type={type === "switch" ? "switch" : "checkbox"}
          value={isBooleanFieldChecked(value)}
          onChange={(next) =>
            onFieldChange(
              field,
              toBooleanFieldValue(
                typeof next === "boolean" ? next : get(next, "target.checked"),
              ),
            )
          }
        />
      );
    }

    const textTypes = {
      text: "simple",
      email: "email",
      phone: "phone",
      textarea: "textarea",
    };
    const inputType = textTypes[type] || "simple";

    return (
      <Input
        {...shared}
        type={inputType}
        value={isNil(value) ? "" : value}
        onChange={(event) => onFieldChange(field, get(event, "target.value"))}
      />
    );
  };

  const onSubmit = async () => {
    const nextErrors = evaluateFormDefinition(
      enrichedFormDefinition,
      values,
      context,
      VALIDATION_MESSAGES,
    );
    const nextTouched = {};
    for (const field of visibleInputFields) {
      nextTouched[field.accessor] = true;
    }
    setTouched((current) => ({ ...current, ...nextTouched }));
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      notification.error({
        message: RUN.REQUIRED_MISSING,
      });
      return;
    }

    setSubmitting(true);
    try {
      for (const accessor of Object.keys(timers.current)) {
        clearTimeout(timers.current[accessor]);
      }
      const touchedAccessors = Object.keys(values);
      for (const accessor of touchedAccessors) {
        await writeField(accessor, values[accessor]);
      }
      await advance({});
    } catch (error) {
      notification.error({
        message: get(error, "message") || RUN.FAILED,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onPrevious = async () => {
    if (!showBack || typeof onBack !== "function") return;
    setGoingBack(true);
    try {
      for (const accessor of Object.keys(timers.current)) {
        clearTimeout(timers.current[accessor]);
      }
      const touchedAccessors = Object.keys(values);
      for (const accessor of touchedAccessors) {
        await writeField(accessor, values[accessor]);
      }
      await onBack();
    } catch (error) {
      notification.error({
        message: get(error, "message") || RUN.FAILED,
      });
    } finally {
      setGoingBack(false);
    }
  };

  const nextLabel = isLastStep ? RUN.FINISH : RUN.NEXT;

  return (
    <Space direction="vertical" size={16} className="canvas-editor__full-width">
      {displayFields.length ? (
        <div>
          {displayFields.map((field) => (
            <div key={field.accessor} className="canvas-editor__field-block">
              <div className="canvas-editor__meta">{field.label}</div>
              <div className="canvas-editor__field-value">
                {formatDisplay(resolveDisplay(field.accessor))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {visibleInputFields.map((field) => (
        <div key={field.accessor}>{renderInput(field)}</div>
      ))}
      <div className="canvas-run__row-wrap">
        {showBack ? (
          <Button
            type="primary--outlined"
            onClick={onPrevious}
            disabled={submitting || goingBack}
          >
            {RUN.PREVIOUS}
          </Button>
        ) : null}
        <Button
          type="primary"
          onClick={onSubmit}
          disabled={submitting || goingBack}
        >
          {nextLabel}
        </Button>
      </div>
    </Space>
  );
};

CanvasForm.propTypes = {
  formDefinition: PropTypes.object,
  project: PropTypes.object,
  data: PropTypes.object,
  advance: PropTypes.func,
  onBack: PropTypes.func,
  showBack: PropTypes.bool,
  isLastStep: PropTypes.bool,
  fieldErrors: PropTypes.object,
  processId: PropTypes.string,
};

CanvasForm.defaultProps = {
  formDefinition: null,
  project: null,
  data: {},
  advance: undefined,
  onBack: undefined,
  showBack: false,
  isLastStep: false,
  fieldErrors: null,
  processId: undefined,
};

export default CanvasForm;
